// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import { Combobox, getDefaultZIndex, Input, Pill, PillsInput, type MantineSize, type Primitive } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { CreatableModal } from 'components/Mantine/TypeSafeSelect/Creatables/CreatableModal';
import { createdKey, creatorKey } from 'components/Mantine/TypeSafeSelect/Creatables/useCreatable';
import { SelectDropdown } from 'components/Mantine/TypeSafeSelect/SelectDropdown';
import { useWrappedSelect } from 'components/Mantine/TypeSafeSelect/useWrappedSelect';
import { isKeyOf } from 'helpers/isKeyOf';
import { weakOmit } from 'helpers/omit';

import { getSelectOptionLabel, optionExists } from './common';
import type { WrappedMultiSelectProps } from './types';

// TODO: There has to be a way to get this from Mantine theme, but I just can't find it
const sizeToRemMap: Record<MantineSize, string> = {
  xs: '1.875rem',
  sm: '2.25rem',
  md: '2.625rem',
  lg: '3.125rem',
  xl: '3.75rem',
} as const;

export const WrappedMultiSelect = <T extends Primitive, D extends object = object>(
  props: WrappedMultiSelectProps<T, D>,
) => {
  const {
    classNames,
    label,
    placeholder,
    size = 'sm',
    nothingFoundMessage = 'No results.',
    nowrap = !label,
    includeCloseButton = !nowrap,
    clearSearchOnChange = true,
    clearSearchOnFocus = true,
    leftSection,
    blurOnChange,
    comboboxProps,
    style,
    loading,
    disabled,
    footer,
    dropdownOpened,
    maxDropdownHeight,
    autoDropdownWidth,
    onScrollToEnd,
    onBlur,
    onKeyDown,
    ref: parentRef,
    value: parentValue,
    defaultValue: parentDefaultValue = [],
    onChange: parentOnChange,
    // Manually excluded vars from `rest`, since they are handled in hooks
    clearable: _1,
    vars: _2,
    ...rest
  } = props;

  const ref = useRef<HTMLInputElement>(null);
  useImperativeHandle(parentRef, () => ref.current as HTMLInputElement, [ref]);

  const groupRef = useRef<HTMLInputElement>(null);

  const [__value, __onChange] = useUncontrolled<T[]>({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: parentOnChange,
  });

  const {
    combobox,
    options,
    searchPlaceholder,
    __searchValue,
    __onSearchChange,
    selectRightSection,
    overrideKeys,
    overrideProps,
  } = useWrappedSelect(
    {
      type: 'multi',
      __value,
      __onChange,
    },
    props,
  );
  const { data, onOptionSubmit, searchable, creationModalProps } = overrideProps;

  const validatedValue = useMemo(
    () =>
      __value
        // Filter out any `value`s that don't exist in the data
        // They should never be passed in in the first place,
        // but this sanity check is sometimes needed.
        .filter((item) => optionExists(data, item)),
    [data, __value],
  );

  // onChange wrapper, essentially
  const onValueSubmit = useCallback<NonNullable<typeof props.onOptionSubmit>>(
    (val) => {
      const index = validatedValue.indexOf(val);
      const newValue =
        index < 0 ? [...validatedValue, val] : [...validatedValue.slice(0, index), ...validatedValue.slice(index + 1)];
      __onChange(newValue);
    },
    [__onChange, validatedValue],
  );

  const values = useMemo(() => {
    const pills = validatedValue.map((item) => (
      <Pill key={String(item)} withRemoveButton onRemove={() => onValueSubmit(item)}>
        {getSelectOptionLabel(data, item)}
      </Pill>
    ));
    if (dropdownOpened || !nowrap || pills.length <= 1) {
      return pills;
    }
    return [pills[0], <Pill key="and-more">+ {pills.length - 1}</Pill>];
  }, [data, dropdownOpened, nowrap, onValueSubmit, validatedValue]);

  const inputWidth = rest.miw ?? rest.maw ?? rest.w ?? 220;

  return (
    <Combobox
      classNames={classNames}
      size={size}
      width={autoDropdownWidth ? 'auto' : undefined}
      {...comboboxProps}
      onOptionSubmit={(val) => {
        onOptionSubmit(val as T);
        if (val !== creatorKey && val !== createdKey) {
          onValueSubmit(val as T);
        }
        if (blurOnChange) {
          combobox.closeDropdown();
          ref.current?.blur();
        }
        if (clearSearchOnChange || val === creatorKey || val === createdKey) {
          __onSearchChange('');
        }
        combobox.updateSelectedOptionIndex('active');
      }}
      store={combobox}
      // Bring up one level so any select inside of a popover should render properly
      zIndex={getDefaultZIndex('popover') + 1}
    >
      <Combobox.Target>
        <PillsInput
          ref={ref}
          label={label}
          disabled={disabled}
          onClick={() => combobox.openDropdown()}
          leftSection={leftSection}
          size={size}
          pointer={!searchable}
          w={nowrap ? inputWidth : 'auto'}
          miw={inputWidth}
          {...weakOmit(rest, overrideKeys)}
          {...selectRightSection}
          style={
            nowrap
              ? // Renders the input on top of other components in-place
                // instead of pushing components down as it grows
                // (but only while open)
                {
                  maxHeight: isKeyOf(size, sizeToRemMap) ? sizeToRemMap[size] : `calc(${size})`,
                  zIndex: combobox.dropdownOpened ? getDefaultZIndex('popover') : undefined,
                  overflowY: 'visible',
                  // TODO: `.style` can now be a function, need to handle it
                  // oxlint-disable-next-line typescript/no-misused-spread
                  ...style,
                }
              : style
          }
        >
          <Pill.Group
            ref={groupRef}
            style={
              !combobox.dropdownOpened && nowrap && values.length > 0
                ? {
                    overflowX: 'scroll',
                    scrollbarWidth: 'none',
                    overflowWrap: 'unset',
                    flexWrap: 'nowrap',
                  }
                : undefined
            }
          >
            {values.length > 0 || searchable ? values : <Input.Placeholder mt={1}>{placeholder}</Input.Placeholder>}

            <Combobox.EventsTarget>
              <PillsInput.Field
                type={searchable ? undefined : 'hidden'}
                onClick={() => combobox.openDropdown()}
                onFocus={() => {
                  if (clearSearchOnFocus) {
                    __onSearchChange('');
                  }
                  combobox.openDropdown();
                }}
                onBlur={(...e) => {
                  combobox.closeDropdown();
                  onBlur?.(...e);
                  groupRef.current?.scrollTo({ left: 0 });
                }}
                placeholder={values.length === 0 ? placeholder : undefined}
                value={searchPlaceholder}
                onChange={(event) => {
                  if (searchable) {
                    combobox.updateSelectedOptionIndex();
                    __onSearchChange(event.currentTarget.value);
                  }
                }}
                onKeyDown={(event) => {
                  onKeyDown?.(event);
                  if (event.key === 'Backspace' && __searchValue.length === 0 && __value.length > 0) {
                    event.preventDefault();
                    // oxlint-disable-next-line typescript/no-non-null-assertion
                    onValueSubmit(__value[__value.length - 1]!);
                  }
                  event.stopPropagation();
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.Target>

      <SelectDropdown
        ref={ref}
        combobox={combobox}
        options={options}
        footer={footer}
        nothingFoundMessage={nothingFoundMessage}
        maxDropdownHeight={maxDropdownHeight}
        includeCloseButton={includeCloseButton}
        loading={loading}
        autoDropdownWidth={autoDropdownWidth}
        onScrollToEnd={onScrollToEnd}
        error={rest.error}
      />

      <CreatableModal creatable={overrideProps.creatable} {...creationModalProps} />
    </Combobox>
  );
};
